import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import {
  getStudyQuestions,
  addQuestionReply,
  markQuestionAsRead,
  deleteQuestion,
  getUserStudies,
  getCTAResponses,
  getStudyById
} from '../services/firebase';
import type { Question, Study, UserProfile, Organization, CTAResponse, CTAType } from '../types';
import { formatAuthorName } from '../utils/formatters';
import { Pagination, getPaginatedItems, getTotalPages, ITEMS_PER_PAGE } from './ui/Pagination';

interface ResponsesAdminProps {
  userId: string;
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
}

type TabType = 'questions' | 'cta';

const CTA_TYPE_LABELS: Record<CTAType, string> = {
  email_signup: 'Email Signup',
  account_creation: 'Account Creation',
  church_invite: 'Church Invite',
  free_book: 'Free Book',
  event_rsvp: 'Event RSVP',
};

const CTA_TYPE_ICONS: Record<CTAType, React.ReactNode> = {
  email_signup: <Icons.Mail className="w-4 h-4" />,
  account_creation: <Icons.User className="w-4 h-4" />,
  church_invite: <Icons.Globe className="w-4 h-4" />,
  free_book: <Icons.BookOpen className="w-4 h-4" />,
  event_rsvp: <Icons.Calendar className="w-4 h-4" />,
};

export const ResponsesAdmin: React.FC<ResponsesAdminProps> = ({
  userId,
  user,
  organization,
  onLogout
}) => {
  const navigate = useNavigate();
  const [activeHeaderTab, setActiveHeaderTab] = useState('admin');
  const [activeTab, setActiveTab] = useState<TabType>('questions');

  // Questions state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [selectedStudy, setSelectedStudy] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unread' | 'read'>('all');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [questionStudies, setQuestionStudies] = useState<Record<string, Study>>({});

  // CTA state
  const [ctaResponses, setCtaResponses] = useState<CTAResponse[]>([]);
  const [ctaLoading, setCtaLoading] = useState(true);
  const [ctaStudies, setCtaStudies] = useState<Record<string, Study>>({});
  const [filterType, setFilterType] = useState<CTAType | 'all'>('all');
  const [filterCtaStudy, setFilterCtaStudy] = useState<string>('all');
  const [expandedResponse, setExpandedResponse] = useState<string | null>(null);

  // Pagination state
  const [questionsPage, setQuestionsPage] = useState(1);
  const [ctaPage, setCtaPage] = useState(1);

  useEffect(() => {
    loadQuestionsAndStudies();
  }, [userId]);

  useEffect(() => {
    if (organization) {
      loadCtaResponses();
    }
  }, [organization]);

  // Questions loading
  const loadQuestionsAndStudies = async () => {
    try {
      setQuestionsLoading(true);
      const userStudies = await getUserStudies(userId);
      const studiesMap: Record<string, Study> = {};
      userStudies.forEach(study => {
        studiesMap[study.id] = study;
      });
      setQuestionStudies(studiesMap);

      const allQuestions: Question[] = [];
      for (const study of userStudies) {
        const studyQuestions = await getStudyQuestions(study.id);
        allQuestions.push(...studyQuestions);
      }
      allQuestions.sort((a, b) => b.createdAt - a.createdAt);
      setQuestions(allQuestions);
    } catch (error) {
      console.error('Failed to load questions:', error);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // CTA loading
  const loadCtaResponses = async () => {
    if (!organization) return;
    setCtaLoading(true);
    try {
      const responsesData = await getCTAResponses(organization.id);
      setCtaResponses(responsesData);

      // Get unique study IDs from responses and fetch each study directly
      const uniqueStudyIds = [...new Set(responsesData.map(r => r.studyId))];
      const studiesMap: Record<string, Study> = {};

      // Fetch studies in parallel by ID (handles private studies too)
      const studyPromises = uniqueStudyIds.map(async (studyId) => {
        const study = await getStudyById(studyId);
        if (study) {
          studiesMap[study.id] = study;
        }
      });
      await Promise.all(studyPromises);

      setCtaStudies(studiesMap);
    } catch (error) {
      console.error('Failed to load CTA responses:', error);
    } finally {
      setCtaLoading(false);
    }
  };

  // Questions handlers
  const handleReply = async (questionId: string) => {
    if (!replyText.trim()) return;
    setSubmittingReply(true);
    try {
      await addQuestionReply(questionId, userId, user.displayName || 'Author', replyText);
      await loadQuestionsAndStudies();
      setReplyingTo(null);
      setReplyText('');
    } catch (error) {
      console.error('Failed to add reply:', error);
      alert('Failed to send reply. Please try again.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleMarkAsRead = async (questionId: string) => {
    try {
      await markQuestionAsRead(questionId);
      await loadQuestionsAndStudies();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDelete = async (questionId: string) => {
    if (!confirm('Are you sure you want to delete this question?')) return;
    try {
      await deleteQuestion(questionId);
      await loadQuestionsAndStudies();
    } catch (error) {
      console.error('Failed to delete question:', error);
      alert('Failed to delete question. Please try again.');
    }
  };

  const getSegmentInfo = (studyId: string, segmentId: string): string => {
    const study = questionStudies[studyId];
    if (!study) return 'Unknown Study';
    const segmentIndex = study.segments.findIndex(s => s.id === segmentId);
    if (segmentIndex === -1) return '(Deleted Segment)';
    return `Segment #${segmentIndex + 1} (${study.segments[segmentIndex].type})`;
  };

  // Filtered data
  const filteredQuestions = questions.filter(q => {
    if (selectedStudy !== 'all' && q.studyId !== selectedStudy) return false;
    if (filterStatus === 'unread' && q.isRead) return false;
    if (filterStatus === 'read' && !q.isRead) return false;
    return true;
  });

  const filteredCtaResponses = ctaResponses.filter(r => {
    if (filterType !== 'all' && r.ctaType !== filterType) return false;
    if (filterCtaStudy !== 'all' && r.studyId !== filterCtaStudy) return false;
    return true;
  });

  const unreadCount = questions.filter(q => !q.isRead).length;
  const uniqueQuestionStudyIds = Array.from(new Set(questions.map(q => q.studyId)));
  const studiesWithCtaResponses = [...new Set(ctaResponses.map(r => r.studyId))];

  // Reset pagination when filters change
  useEffect(() => {
    setQuestionsPage(1);
  }, [selectedStudy, filterStatus]);

  useEffect(() => {
    setCtaPage(1);
  }, [filterType, filterCtaStudy]);

  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getCtaStudyTitle = (studyId: string): string => {
    return ctaStudies[studyId]?.title || 'Unknown Study';
  };

  const getCtaConfig = (studyId: string, segmentId: string) => {
    const study = ctaStudies[studyId];
    if (!study) return null;
    const segment = study.segments.find(s => s.id === segmentId);
    if (!segment || segment.type !== 'cta') return null;
    return segment.metadata?.ctaConfig || null;
  };

  const renderCtaResponseDetails = (response: CTAResponse) => {
    const data = response.response;

    switch (data.type) {
      case 'email_signup':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icons.Mail className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{data.email}</span>
            </div>
            {data.name && (
              <div className="flex items-center gap-2">
                <Icons.User className="w-4 h-4 text-gray-400" />
                <span>{data.name}</span>
              </div>
            )}
          </div>
        );

      case 'church_invite':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icons.User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{data.name}</span>
            </div>
            {data.email && (
              <div className="flex items-center gap-2">
                <Icons.Mail className="w-4 h-4 text-gray-400" />
                <span>{data.email}</span>
              </div>
            )}
            {data.phone && (
              <div className="flex items-center gap-2">
                <Icons.Phone className="w-4 h-4 text-gray-400" />
                <span>{data.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                data.rsvpStatus === 'yes' ? 'bg-green-100 text-green-700' :
                data.rsvpStatus === 'maybe' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {data.rsvpStatus === 'yes' ? 'Attending' :
                 data.rsvpStatus === 'maybe' ? 'Maybe' : 'Not Attending'}
              </span>
            </div>
            {data.notes && (
              <div className="text-sm text-gray-600 italic">"{data.notes}"</div>
            )}
          </div>
        );

      case 'free_book': {
        const ctaConfig = getCtaConfig(response.studyId, response.segmentId);
        const bookTitle = ctaConfig && 'bookTitle' in ctaConfig ? ctaConfig.bookTitle : null;
        return (
          <div className="space-y-2">
            {bookTitle && (
              <div className="flex items-center gap-2 bg-blue-50 px-2 py-1 rounded">
                <Icons.BookOpen className="w-4 h-4 text-primary" />
                <span className="font-medium text-primary">{bookTitle}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Icons.User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{data.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icons.Mail className="w-4 h-4 text-gray-400" />
              <span>{data.email}</span>
            </div>
            {data.phone && (
              <div className="flex items-center gap-2">
                <Icons.Phone className="w-4 h-4 text-gray-400" />
                <span>{data.phone}</span>
              </div>
            )}
            <div className="text-sm text-gray-600">
              <div className="font-medium text-gray-700">Mailing Address:</div>
              <div>{data.address.street}</div>
              <div>{data.address.city}, {data.address.state} {data.address.zipCode}</div>
              {data.address.country && <div>{data.address.country}</div>}
            </div>
          </div>
        );
      }

      case 'event_rsvp':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Icons.User className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{data.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icons.Mail className="w-4 h-4 text-gray-400" />
              <span>{data.email}</span>
            </div>
            {data.phone && (
              <div className="flex items-center gap-2">
                <Icons.Phone className="w-4 h-4 text-gray-400" />
                <span>{data.phone}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                data.rsvpStatus === 'yes' ? 'bg-green-100 text-green-700' :
                data.rsvpStatus === 'maybe' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {data.rsvpStatus === 'yes' ? 'Attending' :
                 data.rsvpStatus === 'maybe' ? 'Maybe' : 'Not Attending'}
              </span>
              {data.guestCount && data.guestCount > 0 && (
                <span className="text-sm text-gray-600">+{data.guestCount} guests</span>
              )}
            </div>
            {data.notes && (
              <div className="text-sm text-gray-600 italic">"{data.notes}"</div>
            )}
          </div>
        );

      default:
        return <div className="text-gray-500">No response data</div>;
    }
  };

  // CTA stats
  const ctaStats = {
    total: ctaResponses.length,
    thisWeek: ctaResponses.filter(r => {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      return r.respondedAt > weekAgo;
    }).length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeHeaderTab}
        setActiveTab={setActiveHeaderTab}
      />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <Icons.ChevronLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Icons.MessageSquare className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Responses</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                {unreadCount} unread
              </span>
            )}
          </div>
          <p className="text-gray-600">
            Manage questions and call-to-action responses from your studies
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('questions')}
            className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 border-2 ${
              activeTab === 'questions'
                ? 'bg-primary text-white border-primary shadow-md'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary'
            }`}
          >
            <Icons.MessageSquare className="w-4 h-4" />
            Questions
            {unreadCount > 0 && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === 'questions' ? 'bg-white text-primary' : 'bg-red-500 text-white'
              }`}>
                {unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('cta')}
            className={`px-5 py-2.5 rounded-lg font-medium text-sm transition-all flex items-center gap-2 border-2 ${
              activeTab === 'cta'
                ? 'bg-primary text-white border-primary shadow-md'
                : 'bg-white text-gray-600 border-gray-200 hover:border-primary hover:text-primary'
            }`}
          >
            <Icons.Clipboard className="w-4 h-4" />
            CTA Responses
            {ctaStats.total > 0 && (
              <span className="bg-gray-200 text-gray-700 text-xs px-1.5 py-0.5 rounded-full">
                {ctaStats.total}
              </span>
            )}
          </button>
        </div>

        {/* Questions Tab Content */}
        {activeTab === 'questions' && (
          <>
            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Study
                  </label>
                  <select
                    value={selectedStudy}
                    onChange={(e) => setSelectedStudy(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="all">All Studies ({uniqueQuestionStudyIds.length})</option>
                    {uniqueQuestionStudyIds.map(studyId => (
                      <option key={studyId} value={studyId}>
                        {questionStudies[studyId]?.title || 'Unknown Study'}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as any)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                  >
                    <option value="all">All Questions</option>
                    <option value="unread">Unread Only</option>
                    <option value="read">Read Only</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Questions List */}
            {questionsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredQuestions.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <Icons.MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No questions yet</h3>
                <p className="text-gray-500 text-sm">
                  {selectedStudy !== 'all' || filterStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Questions from your study viewers will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {getPaginatedItems(filteredQuestions, questionsPage).map((question) => (
                  <div
                    key={question.id}
                    className={`bg-white rounded-lg shadow-sm border-2 transition-all ${
                      question.isRead ? 'border-gray-200' : 'border-blue-500'
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Icons.User className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold text-gray-900">
                              {question.userName || 'Anonymous'}
                            </span>
                            {!question.isRead && (
                              <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 flex flex-wrap items-center gap-2">
                            <span>{new Date(question.createdAt).toLocaleString()}</span>
                            <span>•</span>
                            <span className="font-medium text-primary">
                              {questionStudies[question.studyId]?.title || 'Unknown Study'}
                            </span>
                            <span>•</span>
                            <span>{getSegmentInfo(question.studyId, question.segmentId)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!question.isRead && (
                            <button
                              onClick={() => handleMarkAsRead(question.id)}
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              title="Mark as read"
                            >
                              <Icons.Eye className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(question.id)}
                            className="text-xs text-red-600 hover:text-red-700"
                            title="Delete question"
                          >
                            <Icons.Trash className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-gray-800">{question.questionText}</p>
                      </div>

                      {question.replies && question.replies.length > 0 && (
                        <div className="pl-4 border-l-2 border-blue-500 mb-3 space-y-2">
                          {question.replies.map((reply) => (
                            <div key={reply.id} className="bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-semibold text-blue-900">
                                  {formatAuthorName(reply.authorName)} (You)
                                </span>
                                <span className="text-xs text-gray-500">
                                  {new Date(reply.createdAt).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm text-gray-800">{reply.replyText}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {replyingTo === question.id ? (
                        <div className="border-t pt-3">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply..."
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:outline-none resize-none"
                            rows={3}
                          />
                          <div className="flex items-center justify-end gap-2 mt-2">
                            <button
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText('');
                              }}
                              className="px-4 py-2 text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => handleReply(question.id)}
                              disabled={submittingReply || !replyText.trim()}
                              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-900 disabled:opacity-50 flex items-center gap-2"
                            >
                              {submittingReply ? (
                                <>
                                  <Icons.Loader className="w-4 h-4 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Icons.Send className="w-4 h-4" />
                                  Send Reply
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setReplyingTo(question.id)}
                          className="text-sm text-primary hover:text-blue-900 font-medium flex items-center gap-2"
                        >
                          <Icons.MessageSquare className="w-4 h-4" />
                          Reply to this question
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                <Pagination
                  currentPage={questionsPage}
                  totalPages={getTotalPages(filteredQuestions.length)}
                  totalItems={filteredQuestions.length}
                  itemsPerPage={ITEMS_PER_PAGE}
                  onPageChange={setQuestionsPage}
                  itemLabel="questions"
                />
              </div>
            )}
          </>
        )}

        {/* CTA Tab Content */}
        {activeTab === 'cta' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-gray-900">{ctaStats.total}</div>
                <div className="text-sm text-gray-500">Total Responses</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-green-600">{ctaStats.thisWeek}</div>
                <div className="text-sm text-gray-500">This Week</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-blue-600">
                  {ctaResponses.filter(r => r.ctaType === 'email_signup').length}
                </div>
                <div className="text-sm text-gray-500">Email Signups</div>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <div className="text-2xl font-bold text-purple-600">
                  {ctaResponses.filter(r => r.ctaType === 'church_invite' || r.ctaType === 'event_rsvp').length}
                </div>
                <div className="text-sm text-gray-500">RSVPs</div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as CTAType | 'all')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Types</option>
                  {Object.entries(CTA_TYPE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Study</label>
                <select
                  value={filterCtaStudy}
                  onChange={(e) => setFilterCtaStudy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="all">All Studies</option>
                  {studiesWithCtaResponses.map(studyId => (
                    <option key={studyId} value={studyId}>{getCtaStudyTitle(studyId)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* CTA Responses List */}
            {ctaLoading ? (
              <div className="flex items-center justify-center py-12">
                <Icons.Loader className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredCtaResponses.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
                <Icons.Clipboard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Responses Yet</h3>
                <p className="text-gray-500">
                  {ctaResponses.length === 0
                    ? "CTA responses from your studies will appear here."
                    : "No responses match your current filters."}
                </p>
              </div>
            ) : (
              <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {getPaginatedItems(filteredCtaResponses, ctaPage).map((response) => (
                    <div key={response.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div
                        className="flex items-start justify-between cursor-pointer"
                        onClick={() => setExpandedResponse(
                          expandedResponse === response.id ? null : response.id
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${
                            response.ctaType === 'email_signup' ? 'bg-blue-100 text-blue-600' :
                            response.ctaType === 'church_invite' ? 'bg-green-100 text-green-600' :
                            response.ctaType === 'free_book' ? 'bg-purple-100 text-purple-600' :
                            response.ctaType === 'event_rsvp' ? 'bg-orange-100 text-orange-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>
                            {CTA_TYPE_ICONS[response.ctaType]}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {CTA_TYPE_LABELS[response.ctaType]}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getCtaStudyTitle(response.studyId)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-gray-500 text-right">
                            {formatDate(response.respondedAt)}
                          </div>
                          <Icons.Next className={`w-5 h-5 text-gray-400 transition-transform ${
                            expandedResponse === response.id ? 'rotate-90' : ''
                          }`} />
                        </div>
                      </div>

                      {expandedResponse === response.id && (
                        <div className="mt-4 pt-4 border-t border-gray-100 pl-12">
                          {renderCtaResponseDetails(response)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <Pagination
                currentPage={ctaPage}
                totalPages={getTotalPages(filteredCtaResponses.length)}
                totalItems={filteredCtaResponses.length}
                itemsPerPage={ITEMS_PER_PAGE}
                onPageChange={setCtaPage}
                itemLabel="responses"
              />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
